import React, { useId } from "react";
import { Card, CardHeader, CardLabel } from "./ui/Card";
import { Hatch } from "./ui/Hatch";
import { Pill, PillGroup } from "./ui/Pill";
import { BODY_MAX, IMAGE_HINT, ROUTES, TITLE_MAX } from "./broadcastEnums";
import { errorFor, resolveDeepLink } from "./broadcastDraft";

/**
 * The popup half of the composer: title, description, image and destination.
 *
 * ---------------------------------------------------------------------------
 * NOTHING HERE IS AN EMAIL FIELD
 * ---------------------------------------------------------------------------
 * The title used to double as the email subject and the description as the
 * preheader, and the helper copy had to say so. It does not any more. The email
 * is written in its own card, from its own fields, and this card is rendered
 * only when the author is composing a popup — so every line of copy on it can
 * finally talk about one surface.
 *
 * The 60-character limit stays anyway: it is the wire's `notification_title`
 * ceiling whichever mode fills it, and a lock screen truncates near there too.
 *
 * Purely presentational. Every value arrives as a prop, nothing here fetches,
 * and the only state it touches is the caller's via `onPatch`.
 */

/* ── Field chrome ─────────────────────────────────────────────────────── */

/**
 * The title reads as a headline, not as a form field: large, tracked in, and
 * underlined rather than boxed. Boxing it would give it the same weight as the
 * description, and it is the more consequential of the two.
 */
const TITLE_INPUT =
  "mt-2 w-full bg-transparent text-[19px] font-medium tracking-[-0.015em] " +
  "text-bc-text placeholder:text-bc-faintest border-b border-bc-border pb-2 " +
  "focus:outline-none focus:border-orange-primary transition-colors";

const BODY_INPUT =
  "mt-2 w-full resize-none rounded-lg border border-bc-border bg-bc-subtle " +
  "px-3 py-2.5 text-sm text-bc-text placeholder:text-bc-faintest " +
  "focus:outline-none focus:border-orange-primary transition-colors";

const HELPER_ROW = "mt-1.5 flex items-start justify-between gap-3";
const HELPER_TEXT = "text-xs text-bc-soft";

const SECTION = "mt-6";

/* ── Pieces ───────────────────────────────────────────────────────────── */

/**
 * The live character counter, e.g. `21/60`.
 *
 * Turns red AT the limit rather than past it. `maxLength` stops typing at the
 * boundary, so "past" is a state the keyboard cannot reach — a counter that
 * only reddens at 61 would never redden at all.
 *
 * @param {Object} props
 * @param {number} props.length - Current character count.
 * @param {number} props.max - The limit, `TITLE_MAX` or `BODY_MAX`.
 * @param {string} props.testId - Stable hook for tests.
 * @returns {React.ReactElement}
 */
const Counter = ({ length, max, testId }) => (
  <span
    data-testid={testId}
    className={`shrink-0 font-mono tabular-nums text-xs ${
      length >= max ? "text-bc-bad" : "text-bc-faint"
    }`}
  >
    {length}/{max}
  </span>
);

/**
 * A field-level validation message.
 *
 * Renders nothing at all when there is no message, so the `aria-describedby`
 * the field points at simply does not resolve — which is correct, and quieter
 * than an empty live region.
 *
 * @param {Object} props
 * @param {string} props.id - Target of the field's `aria-describedby`.
 * @param {string} [props.message] - From `errorFor(errors, field)`.
 * @returns {React.ReactElement|null}
 */
const FieldError = ({ id, message }) =>
  message ? (
    <div id={id} role="alert" className="mt-1.5 text-xs text-bc-bad">
      {message}
    </div>
  ) : null;

/**
 * The disabled image placeholder.
 *
 * Upload is a later phase. Until then this states the fact rather than
 * offering a dropzone that does nothing — a dead affordance is worse than an
 * honest absence. The `imageSlot` prop is the seam the real dropzone drops
 * into; nothing else in this file has to change when it arrives.
 *
 * @returns {React.ReactElement}
 */
const ImagePlaceholder = () => (
  <div
    data-testid="bc-image-placeholder"
    className="mt-2 flex items-center gap-3 rounded-lg border border-dashed border-bc-dashed p-3"
  >
    <Hatch className="h-[72px] w-28 shrink-0" />
    <div className="min-w-0">
      <div className="text-xs text-bc-soft">
        Image upload arrives with the next change
      </div>
      <div className="mt-1 font-mono text-[11px] text-bc-faint">{IMAGE_HINT}</div>
    </div>
  </div>
);

/* ── The card ─────────────────────────────────────────────────────────── */

/**
 * Push content: what the broadcast says and where tapping it lands.
 *
 * @param {Object} props
 * @param {Object} props.draft - The draft from `broadcastDraft.js`. Reads `title`, `body`, `routeKey`.
 * @param {(partial: Object) => void} props.onPatch - Merges a partial into the draft. Never called with a whole draft.
 * @param {Array<{ field: string, message: string }>} [props.errors] - The array from `validateDraft`.
 * @param {React.ReactNode} [props.imageSlot] - The image control. Omit for the disabled placeholder; this is the seam the real dropzone lands in.
 * @param {string} [props.className] - Extra classes on the card, merged last.
 * @returns {React.ReactElement}
 */
export const PushContentCard = ({
  draft,
  onPatch,
  errors,
  imageSlot,
  className = "",
}) => {
  const uid = useId();
  const titleErrorId = `${uid}-title-error`;
  const bodyErrorId = `${uid}-body-error`;
  const routeErrorId = `${uid}-route-error`;

  const title = draft?.title || "";
  const body = draft?.body || "";

  const titleError = errorFor(errors, "title");
  const bodyError = errorFor(errors, "body");
  const routeError = errorFor(errors, "routeKey");

  // Display only. The payload sends the route id; this string is here so the
  // author can see what the tap actually resolves to.
  const deepLink = resolveDeepLink(draft);

  const patch = (partial) => {
    if (onPatch) onPatch(partial);
  };

  return (
    <Card className={`p-5 ${className}`} as="section">
      <CardHeader
        title="Popup content"
        subtitle="One glanceable line, and the screen a tap lands on."
        actions={
          <span className="rounded-full bg-light-orange px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-bc-accent-text">
            Popup
          </span>
        }
      />

      {/* 1 · Title — the bold line on the lock screen. */}
      <div className="mt-4">
        <CardLabel>Title</CardLabel>
        <input
          type="text"
          value={title}
          maxLength={TITLE_MAX}
          placeholder="New workouts are live"
          aria-label="Title"
          aria-invalid={titleError ? "true" : undefined}
          aria-describedby={titleError ? titleErrorId : undefined}
          onChange={(event) => patch({ title: event.target.value })}
          className={TITLE_INPUT}
        />
        <div className={HELPER_ROW}>
          <span className={HELPER_TEXT}>
            The bold first line of the notification.
          </span>
          <Counter
            length={title.length}
            max={TITLE_MAX}
            testId="bc-title-counter"
          />
        </div>
        <FieldError id={titleErrorId} message={titleError} />
      </div>

      {/* 2 · Description — the line under the title. */}
      <div className={SECTION}>
        <CardLabel>Description</CardLabel>
        <textarea
          rows={4}
          value={body}
          maxLength={BODY_MAX}
          placeholder="Three new strength sessions dropped this week — take a look."
          aria-label="Description"
          aria-invalid={bodyError ? "true" : undefined}
          aria-describedby={bodyError ? bodyErrorId : undefined}
          onChange={(event) => patch({ body: event.target.value })}
          className={BODY_INPUT}
        />
        <div className={HELPER_ROW}>
          <span className={HELPER_TEXT}>
            Sits under the title. The OS trims it if it runs long.
          </span>
          <Counter
            length={body.length}
            max={BODY_MAX}
            testId="bc-body-counter"
          />
        </div>
        <FieldError id={bodyErrorId} message={bodyError} />
      </div>

      {/* 3 · Image. `imageSlot` is the seam; the placeholder is the default. */}
      <div className={SECTION}>
        <CardLabel>Image</CardLabel>
        {imageSlot ? <div className="mt-2">{imageSlot}</div> : <ImagePlaceholder />}
      </div>

      {/* 4 · Destination — six closed values, no free-text link. */}
      <div className={SECTION}>
        <CardLabel>Opens</CardLabel>
        <PillGroup label="Destination" className="mt-2">
          {ROUTES.map((route) => (
            <Pill
              key={route.id}
              selected={draft?.routeKey === route.id}
              onSelect={() => patch({ routeKey: route.id })}
            >
              {route.label}
            </Pill>
          ))}
        </PillGroup>
        {/* An unrecognised route resolves to "", and "Deep link · " with
            nothing after it is worse than no line: it reads as a link that
            failed to load rather than as a destination not yet chosen. */}
        {deepLink ? (
          <div
            data-testid="bc-deep-link"
            className="mt-2 font-mono text-xs text-bc-accent-text"
          >
            Deep link · {deepLink}
          </div>
        ) : null}
        {/* No `aria-describedby` here: the describable element is the
            radiogroup, and `PillGroup` is a shared primitive this card does
            not get to grow a prop on. `role="alert"` still announces it. */}
        <FieldError id={routeErrorId} message={routeError} />
      </div>
    </Card>
  );
};

export default PushContentCard;
